import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import html2pdf from 'html2pdf.js';
import { palette } from '../../../Convoze.theme';
import {
  getNipponRatingLabel,
  getNipponRatingTone,
  nipponPhaseNotes,
  nipponRatingLegendItems,
  nipponRatingNaItem
} from './nipponRatingLegend';
import NipponOnePager from './NipponOnePager';

const PASS_COLOR = { backgroundColor: palette.positiveBg, color: palette.positive };
const FAIL_COLOR = { backgroundColor: palette.negativeBg, color: palette.negative };
const NEUTRAL_COLOR = { backgroundColor: palette.neutralBg, color: palette.neutral };
// N/A has no dedicated palette entry — reusing neutral/neutralBg would collide with the
// "Moderate" rating tier, so this is an ink-derived muted grey instead of an invented hex.
const NA_COLOR = { backgroundColor: 'rgba(22,22,25,0.06)', color: 'rgba(22,22,25,0.65)' };
const RATING_TIER_COLORS = { negative: FAIL_COLOR, neutral: NEUTRAL_COLOR, positive: PASS_COLOR, na: NA_COLOR };

const PHASE_HEADER_SX = {
  mb: 1.5,
  p: 2,
  border: '1px solid rgba(22,22,25,0.08)',
  borderLeft: `4px solid ${palette.primary}`,
  borderRadius: 3,
  backgroundColor: palette.white,
  pageBreakAfter: 'avoid',
  breakAfter: 'avoid-page'
};

const PROFILE_HIGHLIGHTS = [
  'Candidate Name',
  'PAN Number',
  'Location/Area',
  'City',
  'Current Employer',
  'Industry Experience',
  'Current Annual CTC',
  'Business/Premium Value'
];

const CRITICAL_NA_COVERAGE_THRESHOLD = 0.4;
const RATED_PHASE_NAMES = ['CompetencyAssessment', 'M0ReadinessAssessment'];

const normalizeLabel = (value, fallback = 'N/A') => {
  const text = String(value || '').trim();
  return text || fallback;
};

const getChipStyle = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['pass', 'true', 'yes'].includes(normalized)) return PASS_COLOR;
  if (['fail', 'false', 'no'].includes(normalized)) return FAIL_COLOR;
  return NA_COLOR;
};

const displayValue = (_key, value) => value || 'Not Mentioned';

const phaseTitleMap = {
  ProfileDataCollection: 'Phase 1: Profile Data Collection',
  CompetencyAssessment: 'Phase 2: Competency Assessment',
  PeerReferenceExtraction: 'Phase 3: Peer Reference Extraction',
  M0ReadinessAssessment: 'Phase 4: M0 Readiness Assessment',
  ResumeProfileVerification: 'Phase 5: Resume Profile Verification'
};

const getPhaseNote = (phaseName) => nipponPhaseNotes[phaseName] || '';

const sanitizeFileNamePart = (value) => String(value || '')
  .trim()
  .replace(/[^a-zA-Z0-9]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '') || 'unknown';

const getReportDatePart = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown-date';
  return date.toISOString().slice(0, 10);
};

const getReportDateLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const normalizePhases = (phases = []) => phases
  .filter(Boolean)
  .map((entry) => {
    const phaseName = Object.keys(entry || {})[0];
    return phaseName ? { phaseName, phase: entry[phaseName] } : null;
  })
  .filter(Boolean);

const countCompliance = (subParameters = []) => subParameters.reduce((acc, sub) => {
  const result = String(sub?.result || sub?.issueFlagged || sub?.applicability || 'N/A').trim().toLowerCase();
  if (['pass', 'true', 'yes'].includes(result)) acc.pass += 1;
  else if (['fail', 'false', 'no'].includes(result)) acc.fail += 1;
  else acc.na += 1;
  return acc;
}, { pass: 0, fail: 0, na: 0 });

const getRowKey = (sub) => sub?.parameterIndex || sub?.parameter;
const normalizeComparison = (comparison) => {
  if (!comparison || typeof comparison !== 'object') return null;
  return {
    status: String(comparison.status || '').trim() || 'pending',
    fields: Array.isArray(comparison.fields) ? comparison.fields : [],
    summary: comparison.summary || {}
  };
};

const PROFILE_COMPARISON_LABEL_MAP = {
  'Candidate Name': 'Candidate Name',
  'Email Address': 'Email Address',
  'Current Employer': 'Current Employer',
  'Current Role': 'Current Role',
  'Location/Area': 'Location',
  City: 'Location',
  'Current Location': 'Location',
  Location: 'Location',
  'Total Experience': 'Total Experience',
  Experience: 'Total Experience',
  'Industry Experience': 'Total Experience'
};

const buildComparisonLookup = (comparison) => {
  const fields = Array.isArray(comparison?.fields) ? comparison.fields : [];
  return new Map(
    fields
      .map((field) => [String(field?.label || field?.fieldKey || '').trim(), field])
      .filter(([label]) => label)
  );
};

// Computed roll-up across all phases — derived analysis, not part of the original
// AI-generated report data. Kept clearly labeled as such wherever it's rendered.
const getExecutiveSummary = (normalizedPhases) => {
  const totals = normalizedPhases.reduce((acc, { phase }) => {
    const subParameters = Array.isArray(phase?.subParameters) ? phase.subParameters : [];
    const compliance = countCompliance(subParameters);
    acc.pass += compliance.pass;
    acc.fail += compliance.fail;
    acc.na += compliance.na;
    return acc;
  }, { pass: 0, fail: 0, na: 0 });

  const numericScores = new Set(nipponRatingLegendItems.map((item) => item.score));

  let ratingSum = 0;
  let ratingCount = 0;
  let criticalTopicCount = 0;
  let criticalNaCount = 0;

  normalizedPhases.forEach(({ phaseName, phase }) => {
    if (!RATED_PHASE_NAMES.includes(phaseName)) return;
    const subParameters = Array.isArray(phase?.subParameters) ? phase.subParameters : [];
    subParameters.forEach((sub) => {
      criticalTopicCount += 1;
      const ratingText = String(sub?.rating ?? '').trim();
      if (numericScores.has(ratingText)) {
        ratingSum += Number(ratingText);
        ratingCount += 1;
      } else {
        criticalNaCount += 1;
      }
    });
  });

  const averageRating = ratingCount ? ratingSum / ratingCount : null;
  const naCoverageRatio = criticalTopicCount ? criticalNaCount / criticalTopicCount : 0;

  return {
    totals,
    averageRating,
    ratingCount,
    criticalTopicCount,
    criticalNaCount,
    naCoverageRatio,
    coverageFlag: criticalTopicCount > 0 && naCoverageRatio >= CRITICAL_NA_COVERAGE_THRESHOLD
  };
};

const resultCell = (value) => {
  const label = normalizeLabel(value);
  return (
    <Box
      component="span"
      sx={{
        ...getChipStyle(value),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 72,
        px: 1.25,
        py: 0.4,
        borderRadius: 999,
        fontSize: '0.75rem',
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        border: '1px solid rgba(0,0,0,0.06)'
      }}
    >
      {label}
    </Box>
  );
};

const renderRatingCell = (ratingValue) => {
  const tone = getNipponRatingTone(ratingValue);
  const fillColor = RATING_TIER_COLORS[tone.tier]?.color || NA_COLOR.color;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'inline-flex', gap: '3px' }}>
        {Array.from({ length: 5 }).map((_, dotIndex) => (
          <Box
            key={dotIndex}
            component="span"
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: dotIndex < tone.dotCount ? fillColor : 'rgba(22,22,25,0.12)'
            }}
          />
        ))}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{getNipponRatingLabel(ratingValue)}</Typography>
    </Box>
  );
};

const commonTableSx = {
  '& .MuiTableRow-root': {
    breakInside: 'avoid',
    pageBreakInside: 'avoid'
  },
  '& .MuiTableCell-root': {
    verticalAlign: 'top',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    whiteSpace: 'normal'
  },
  '& tbody .MuiTableRow-root:nth-of-type(odd)': {
    backgroundColor: palette.white
  }
};

const renderProfileTable = (subParameters = [], comparisonLookup = new Map()) => (
  <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'hidden' }}>
    <Table size="small" sx={commonTableSx}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700, width: '20%' }}>Field</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '18%' }}>Extracted Data</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '18%' }}>Resume Value</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '12%' }}>Match</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '32%' }}>Evidence</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {subParameters.filter((sub) => sub?.parameter !== 'Mobile Number').map((sub) => {
          const comparisonLabel = PROFILE_COMPARISON_LABEL_MAP[sub?.parameter];
          const comparisonField = comparisonLabel ? comparisonLookup.get(comparisonLabel) : null;
          return (
            <TableRow key={getRowKey(sub)}>
              <TableCell>{sub?.parameter}</TableCell>
              <TableCell>{displayValue(sub?.parameter, sub?.extractedValue)}</TableCell>
              <TableCell>{comparisonField?.resumeValue || 'Not Mentioned'}</TableCell>
              <TableCell>{resultCell(comparisonField?.matchResult || 'N/A')}</TableCell>
              <TableCell>{sub?.evidence || sub?.reason || 'Not Mentioned'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const renderPeerTable = (subParameters = []) => (
  <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'hidden' }}>
    <Table size="small" sx={commonTableSx}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700, width: '16%' }}>Peer</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '18%' }}>Current Company</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '16%' }}>Trait</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '12%' }}>Compliance</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '24%' }}>Evidence</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {subParameters.map((sub) => {
          const extracted = sub?.extractedValue || {};
          return (
            <TableRow key={getRowKey(sub)}>
              <TableCell>{extracted?.name || sub?.parameter}</TableCell>
              <TableCell>{extracted?.currentCompany || 'Not Mentioned'}</TableCell>
              <TableCell>{extracted?.performanceTrait || 'Not Mentioned'}</TableCell>
              <TableCell>{resultCell(sub?.result)}</TableCell>
              <TableCell>{sub?.evidence || sub?.reason || 'Not Mentioned'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const renderAssessmentTable = (subParameters = []) => (
  <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'hidden' }}>
    <Table size="small" sx={commonTableSx}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700, width: '23%' }}>Topic</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '9%' }}>Rating</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '11%' }}>Result</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '13%' }}>Applicability</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '21%' }}>Reason</TableCell>
          <TableCell sx={{ fontWeight: 700, width: '23%' }}>Evidence</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {subParameters.map((sub) => (
          <TableRow key={getRowKey(sub)}>
            <TableCell>
              <Typography sx={{ fontWeight: 600 }}>{sub?.parameter}</Typography>
              <Typography variant="caption" color="text.secondary">{sub?.description}</Typography>
            </TableCell>
            <TableCell>{renderRatingCell(sub?.rating)}</TableCell>
            <TableCell>{resultCell(sub?.result)}</TableCell>
            <TableCell>{resultCell(sub?.applicability)}</TableCell>
            <TableCell>{sub?.reason || 'Not Mentioned'}</TableCell>
            <TableCell>{sub?.evidence || 'Not Mentioned'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// No manual chunking/pagination — each phase renders as one continuous table. html2pdf's
// own pagebreak.avoid list (see handleExportPdf: 'tr', '.summary-card', '.phase-header')
// keeps the browser's natural page-break placement from ever cutting through a row or a
// header, sliding the break to before the element that wouldn't fit instead.
const renderPhaseTable = (phaseName, subParameters, comparisonLookup = new Map()) => {
  if (phaseName === 'ProfileDataCollection') return renderProfileTable(subParameters, comparisonLookup);
  if (phaseName === 'PeerReferenceExtraction') return renderPeerTable(subParameters);
  return renderAssessmentTable(subParameters);
};

const ExecutiveSummary = ({ summary }) => {
  const { totals, averageRating, ratingCount, criticalTopicCount, criticalNaCount, naCoverageRatio, coverageFlag } = summary;
  return (
    <Paper
      variant="outlined"
      className="summary-card"
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 3,
        backgroundColor: palette.white,
        pageBreakInside: 'avoid',
        breakInside: 'avoid-page'
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Executive Summary</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Computed roll-up across all four phases — not part of the original AI-generated evaluation.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5 }}>
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, backgroundColor: palette.positiveBg, borderColor: 'transparent' }}>
          <Typography variant="caption" sx={{ color: palette.positive, display: 'block', mb: 0.5, fontWeight: 700 }}>Pass</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: palette.positive }}>{totals.pass}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, backgroundColor: palette.negativeBg, borderColor: 'transparent' }}>
          <Typography variant="caption" sx={{ color: palette.negative, display: 'block', mb: 0.5, fontWeight: 700 }}>Fail</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: palette.negative }}>{totals.fail}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, backgroundColor: 'rgba(22,22,25,0.05)', borderColor: 'transparent' }}>
          <Typography variant="caption" sx={{ color: 'rgba(22,22,25,0.65)', display: 'block', mb: 0.5, fontWeight: 700 }}>N/A · Not Mentioned</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: palette.night }}>{totals.na}</Typography>
        </Paper>
      </Box>
      {ratingCount ? (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: palette.night }}>
            Rated average: {averageRating.toFixed(1)} / 5
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({ratingCount} numerically-rated {ratingCount === 1 ? 'topic' : 'topics'} across Competency &amp; M0 Readiness)
          </Typography>
        </Box>
      ) : null}
      {coverageFlag ? (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, backgroundColor: palette.neutralBg, border: `1px solid ${palette.neutral}` }}>
          <Typography variant="body2" sx={{ color: palette.night }}>
            <strong>Computed insight:</strong> {criticalNaCount} of {criticalTopicCount} Critical-phase topics ({Math.round(naCoverageRatio * 100)}%) were marked Not Applicable — interview coverage on Competency/M0 Readiness topics is limited.
          </Typography>
        </Box>
      ) : null}
    </Paper>
  );
};

export default function NipponInterviewPhases({ phases = [], reportId, recordingDateTime, uploadedDateTime, fileName, resumeInterviewComparison = null }) {
  const exportRef = useRef(null);
  const [viewMode, setViewMode] = useState('full');
  const normalizedPhases = useMemo(() => normalizePhases(phases), [phases]);
  const normalizedComparison = useMemo(() => normalizeComparison(resumeInterviewComparison), [resumeInterviewComparison]);
  const comparisonLookup = useMemo(() => buildComparisonLookup(normalizedComparison), [normalizedComparison]);
  const profilePhase = normalizedPhases.find((item) => item.phaseName === 'ProfileDataCollection');
  const profileMap = new Map((profilePhase?.phase?.subParameters || []).map((sub) => [sub?.parameter, sub]));
  const summaryFields = PROFILE_HIGHLIGHTS.map((label) => ({
    label,
    value: profileMap.get(label)?.extractedValue ?? 'Not Mentioned'
  }));

  const candidateName = profileMap.get('Candidate Name')?.extractedValue || fileName || 'candidate';
  const reportDate = getReportDatePart(recordingDateTime || uploadedDateTime);
  const reportDateLabel = getReportDateLabel(recordingDateTime || uploadedDateTime);
  const pdfFileName = `Indusind-Nippon-Life-Insurance-Bot-Interview-Report-${reportDate}-${sanitizeFileNamePart(candidateName)}.pdf`;

  const executiveSummary = useMemo(() => getExecutiveSummary(normalizedPhases), [normalizedPhases]);

  const handleExportPdf = () => {
    if (!exportRef.current) return;
    html2pdf()
      .set({
        margin: [8, 8],
        filename: pdfFileName,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: {
          mode: ['css', 'legacy'],
          avoid: ['tr', '.summary-card', '.phase-header']
        }
      })
      .from(exportRef.current)
      .save();
  };

  if (!normalizedPhases.length) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>Interview Evaluation by Phase</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', border: `1px solid ${palette.primaryBg}`, borderRadius: 999, p: 0.4, gap: 0.5 }}>
            <Button
              size="small"
              onClick={() => setViewMode('full')}
              sx={{
                textTransform: 'none',
                borderRadius: 999,
                px: 1.5,
                backgroundColor: viewMode === 'full' ? palette.primary : 'transparent',
                color: viewMode === 'full' ? '#fff' : palette.night,
                '&:hover': { backgroundColor: viewMode === 'full' ? palette.primary : palette.primaryBg }
              }}
            >
              Full Report
            </Button>
            <Button
              size="small"
              onClick={() => setViewMode('onepager')}
              sx={{
                textTransform: 'none',
                borderRadius: 999,
                px: 1.5,
                backgroundColor: viewMode === 'onepager' ? palette.primary : 'transparent',
                color: viewMode === 'onepager' ? '#fff' : palette.night,
                '&:hover': { backgroundColor: viewMode === 'onepager' ? palette.primary : palette.primaryBg }
              }}
            >
              One-Pager
            </Button>
          </Box>
          {viewMode === 'full' ? (
            <Button variant="contained" startIcon={<FileDownloadIcon />} onClick={handleExportPdf} sx={{ textTransform: 'none' }}>
              Download PDF
            </Button>
          ) : null}
        </Box>
      </Box>

      {viewMode === 'onepager' ? (
        <NipponOnePager phases={phases} reportDateLabel={reportDateLabel} resumeInterviewComparison={normalizedComparison} />
      ) : (
      <Box ref={exportRef} sx={{ backgroundColor: '#fff', p: 1.5, width: '100%', maxWidth: '100%', overflow: 'hidden', '& table': { tableLayout: 'fixed', width: '100%' } }}>
        <Box sx={{ mb: 3, pb: 2, borderBottom: `2px solid ${palette.primaryBg}` }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: palette.night, mb: 0.5 }}>
            Indusind Nippon Life Insurance Bot Interview Evaluation Report
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Four-phase preliminary interview extraction, compliance, and readiness review
          </Typography>
          <Paper
            className="summary-card"
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 3,
              backgroundColor: palette.white,
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              rowGap: 2,
              columnGap: 1.5
            }}
          >
            {summaryFields.map((field) => (
              <Box key={field.label}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{field.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: palette.night }}>{displayValue(field.label, field.value)}</Typography>
              </Box>
            ))}
          </Paper>
        </Box>

        <ExecutiveSummary summary={executiveSummary} />

        <Paper
          variant="outlined"
          className="summary-card"
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 3,
            backgroundColor: palette.white,
            pageBreakInside: 'avoid',
            breakInside: 'avoid-page'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Rating Legend</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.25, mb: 1.5 }}>
            {nipponRatingLegendItems.map((item) => {
              const tone = getNipponRatingTone(item.score);
              const stripeColor = RATING_TIER_COLORS[tone.tier]?.color || palette.night;
              return (
                <Paper key={item.score} variant="outlined" sx={{ p: 1.25, pl: 1.5, borderRadius: 2, backgroundColor: '#fff', borderLeft: `3px solid ${stripeColor}` }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: palette.night, mb: 0.5 }}>
                    {item.score} - {item.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.description}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700, color: palette.night }}>
            {nipponRatingNaItem.score} - {nipponRatingNaItem.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {nipponRatingNaItem.description}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {nipponPhaseNotes.CompetencyAssessment}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {nipponPhaseNotes.M0ReadinessAssessment}
          </Typography>
        </Paper>

        {normalizedPhases.map(({ phaseName, phase }) => {
          const subParameters = Array.isArray(phase?.subParameters) ? phase.subParameters : [];
          const compliance = countCompliance(subParameters);
          const phaseNote = getPhaseNote(phaseName);
          return (
            <Box key={phaseName} className="phase-block" sx={{ mb: 2 }}>
              <Box className="phase-header" sx={PHASE_HEADER_SX}>
                <Typography variant="h6" fontWeight={700}>{phaseTitleMap[phaseName] || phaseName}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{phase?.description}</Typography>
                {phaseNote ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                    {phaseNote}
                  </Typography>
                ) : null}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={`Criticality: ${phase?.criticality || 'N/A'}`} sx={{ backgroundColor: palette.primaryBg, color: palette.primary, fontWeight: 700 }} />
                  <Chip size="small" label={`Pass: ${compliance.pass}`} sx={PASS_COLOR} />
                  <Chip size="small" label={`Fail: ${compliance.fail}`} sx={FAIL_COLOR} />
                  <Chip size="small" label={`N/A: ${compliance.na}`} sx={NA_COLOR} />
                </Box>
              </Box>
              {renderPhaseTable(phaseName, subParameters, comparisonLookup)}
            </Box>
          );
        })}
      </Box>
      )}
    </Box>
  );
}

